import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  ArrowDownLeft,
  Clock,
  Eye,
  IndianRupee,
  TrendingUp,
  Wallet,
  CreditCard,
  RefreshCw,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge, getStatusVariant } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { formatCurrency, formatDate, formatRelativeTime, getInitials, exportToCSV } from '@/lib/utils';
import toast from 'react-hot-toast';

interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'game_entry' | 'winnings' | 'bonus' | 'refund';
  amount: number;
  status: 'completed' | 'pending' | 'failed' | 'processing';
  userId: string;
  userName: string;
  userPhone: string;
  method?: string;
  reference?: string;
  description: string;
  createdAt: string;
  completedAt?: string;
}

const mockTransactions: Transaction[] = [];

const typeIcons: Record<string, React.ReactNode> = {
  deposit: <ArrowDownLeft className="w-4 h-4 text-emerald-400" />,
  withdrawal: <ArrowUpRight className="w-4 h-4 text-orange-400" />,
  game_entry: <IndianRupee className="w-4 h-4 text-blue-400" />,
  winnings: <TrendingUp className="w-4 h-4 text-emerald-400" />,
  bonus: <Wallet className="w-4 h-4 text-violet-400" />,
  refund: <RefreshCw className="w-4 h-4 text-amber-400" />,
};

const typeColors: Record<string, string> = {
  deposit: 'text-emerald-400',
  withdrawal: 'text-orange-400',
  game_entry: 'text-red-400',
  winnings: 'text-emerald-400',
  bonus: 'text-violet-400',
  refund: 'text-amber-400',
};

export default function Transactions() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('7d');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTxn, setSelectedTxn] = useState<Transaction | null>(null);
  const itemsPerPage = 15;

  const filteredTransactions = mockTransactions.filter((txn) => {
    const matchesSearch =
      txn.userName.toLowerCase().includes(search.toLowerCase()) ||
      txn.reference?.toLowerCase().includes(search.toLowerCase()) ||
      txn.id.includes(search);
    const matchesType = typeFilter === 'all' || txn.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || txn.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Stats
  const totalDeposits = mockTransactions
    .filter((t) => t.type === 'deposit' && t.status === 'completed')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalWithdrawals = mockTransactions
    .filter((t) => t.type === 'withdrawal' && t.status === 'completed')
    .reduce((sum, t) => sum + t.amount, 0);
  const pendingWithdrawals = mockTransactions.filter(
    (t) => t.type === 'withdrawal' && t.status === 'pending'
  ).length;

  const handleExport = () => {
    exportToCSV(
      filteredTransactions.map((t) => ({
        ID: t.id,
        Type: t.type,
        Amount: t.amount,
        Status: t.status,
        User: t.userName,
        Reference: t.reference,
        Date: t.createdAt,
      })),
      'transactions'
    );
    toast.success('Transactions exported successfully');
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold">Transactions</h1>
          <p className="text-slate-400 mt-1">Monitor all financial transactions</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Deposits"
          value={formatCurrency(totalDeposits)}
          icon={ArrowDownLeft}
          color="emerald"
          change="+12.5%"
        />
        <StatCard
          title="Total Withdrawals"
          value={formatCurrency(totalWithdrawals)}
          icon={ArrowUpRight}
          color="orange"
          change="-5.2%"
        />
        <StatCard
          title="Pending Withdrawals"
          value={pendingWithdrawals.toString()}
          icon={Clock}
          color="amber"
          urgent
        />
        <StatCard
          title="Net Revenue"
          value={formatCurrency(totalDeposits - totalWithdrawals)}
          icon={TrendingUp}
          color="violet"
          change="+8.3%"
        />
      </div>

      {/* Filters */}
      <Card variant="glass" className="p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by user, reference, or transaction ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            >
              <option value="all">All Types</option>
              <option value="deposit">Deposits</option>
              <option value="withdrawal">Withdrawals</option>
              <option value="game_entry">Game Entry</option>
              <option value="winnings">Winnings</option>
              <option value="bonus">Bonus</option>
              <option value="refund">Refund</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
            </select>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            >
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Transactions Table */}
      <Card variant="glass" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Transaction
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  User
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Amount
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Method
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Date
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              <AnimatePresence mode="popLayout">
                {paginatedTransactions.map((txn, index) => (
                  <motion.tr
                    key={txn.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className="hover:bg-slate-800/30 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                          {typeIcons[txn.type]}
                        </div>
                        <div>
                          <p className="font-medium capitalize">{txn.type.replace('_', ' ')}</p>
                          <p className="text-xs text-slate-500 font-mono">{txn.reference}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-xs font-semibold">
                          {getInitials(txn.userName)}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{txn.userName}</p>
                          <p className="text-xs text-slate-500">{txn.userPhone}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`font-semibold ${typeColors[txn.type]}`}>
                        {['withdrawal', 'game_entry'].includes(txn.type) ? '-' : '+'}
                        {formatCurrency(txn.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-slate-400" />
                        <span className="text-sm">{txn.method || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={getStatusVariant(txn.status)}>{txn.status}</Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm">{formatDate(txn.createdAt)}</p>
                        <p className="text-xs text-slate-500">{formatRelativeTime(txn.createdAt)}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setSelectedTxn(txn)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800">
          <p className="text-sm text-slate-400">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, filteredTransactions.length)} of{' '}
            {filteredTransactions.length}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Transaction Detail Modal */}
      <Dialog open={!!selectedTxn} onOpenChange={() => setSelectedTxn(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
            <DialogDescription>Full transaction information</DialogDescription>
          </DialogHeader>
          {selectedTxn && (
            <div className="py-4 space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b border-slate-800">
                <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center">
                  {typeIcons[selectedTxn.type]}
                </div>
                <div>
                  <p className="font-semibold text-lg capitalize">
                    {selectedTxn.type.replace('_', ' ')}
                  </p>
                  <p className="text-sm text-slate-400">{selectedTxn.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <DetailItem label="Transaction ID" value={selectedTxn.id} mono />
                <DetailItem label="Reference" value={selectedTxn.reference || 'N/A'} mono />
                <DetailItem
                  label="Amount"
                  value={formatCurrency(selectedTxn.amount)}
                  highlight={typeColors[selectedTxn.type]}
                />
                <DetailItem label="Status" value={selectedTxn.status} />
                <DetailItem label="User" value={selectedTxn.userName} />
                <DetailItem label="Phone" value={selectedTxn.userPhone} />
                <DetailItem label="Method" value={selectedTxn.method || 'N/A'} />
                <DetailItem label="Date" value={formatDate(selectedTxn.createdAt)} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

function StatCard({
  title,
  value,
  icon: Icon,
  color,
  change,
  urgent,
}: {
  title: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  change?: string;
  urgent?: boolean;
}) {
  const colorClasses: Record<string, { bg: string; text: string; icon: string }> = {
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: 'text-emerald-400' },
    orange: { bg: 'bg-orange-500/10', text: 'text-orange-400', icon: 'text-orange-400' },
    amber: { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: 'text-amber-400' },
    violet: { bg: 'bg-violet-500/10', text: 'text-violet-400', icon: 'text-violet-400' },
  };

  const colors = colorClasses[color] || colorClasses.violet;

  return (
    <Card variant="glass" className="p-4 relative">
      {urgent && (
        <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
      )}
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${colors.icon}`} />
        </div>
        {change && (
          <span
            className={`text-xs font-medium ${
              change.startsWith('+') ? 'text-emerald-400' : 'text-red-400'
            }`}
          >
            {change}
          </span>
        )}
      </div>
      <div className="mt-3">
        <p className={`text-2xl font-bold ${colors.text}`}>{value}</p>
        <p className="text-sm text-slate-400 mt-1">{title}</p>
      </div>
    </Card>
  );
}

function DetailItem({
  label,
  value,
  mono,
  highlight,
}: {
  label: string;
  value: string;
  mono?: boolean;
  highlight?: string;
}) {
  return (
    <div>
      <p className="text-sm text-slate-400">{label}</p>
      <p className={`font-medium capitalize ${mono ? 'font-mono text-sm' : ''} ${highlight || ''}`}>
        {value}
      </p>
    </div>
  );
}
